import React, { useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { AuthContext } from "contexts/authContext";

// react-bootstrap components
import { Collapse, Nav } from "react-bootstrap";
import { useTheme } from "contexts/ThemeContext";

function Sidebar({ routes }) {
  // Contexts
  const currentUser = useContext(AuthContext);
  const { isDark } = useTheme();
  const groupName = currentUser.groupName;
  const userClientId = currentUser.clientId;
  const clientName = currentUser.clientName;

  // to check for active links and opened collapses
  let location = useLocation();
  // this is for the rest of the collapses
  const [state, setState] = React.useState({});
  React.useEffect(() => {
    setState(getCollapseStates(routes));
  }, []);
  // this creates the intial state of this component based on the collapse routes
  // that it gets through routes prop
  const getCollapseStates = (routes) => {
    let initialState = {};
    routes.map((prop, key) => {
      if (prop.collapse) {
        initialState = {
          [prop.state]: getCollapseInitialState(prop.views),
          ...getCollapseStates(prop.views),
          ...initialState,
        };
      }
      return null;
    });
    return initialState;
  };
  // this verifies if any of the collapses should be default opened on a rerender of this component
  // for example, on the refresh of the page,
  // while on the src/views/forms/RegularForms.jsx - route /admin/regular-forms
  const getCollapseInitialState = (routes) => {
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse && getCollapseInitialState(routes[i].views)) {
        return true;
      } else if (location.pathname === routes[i].layout + routes[i].path) {
        return true;
      }
    }
    return false;
  };
  // this function creates the links and collapses that appear in the sidebar (left menu)
  const createLinks = (routes) => {
    return routes.map((prop, key) => {
      if (prop.redirect) {
        return null;
      }
      if (prop.collapse) {
        const st = {};
        st[prop["state"]] = !state[prop.state];
        return (
          <Nav.Item
            className={getCollapseInitialState(prop.views) ? "active" : ""}
            as="li"
            key={key}
          >
            <Nav.Link
              className={state[prop.state] ? "collapsed" : ""}
              data-toggle="collapse"
              onClick={(e) => {
                e.preventDefault();
                setState({ ...state, ...st });
              }}
              aria-expanded={state[prop.state]}
            >
              <i className={prop.icon}></i>
              <p>
                {prop.name} <b className="caret"></b>
              </p>
            </Nav.Link>
            <Collapse in={state[prop.state]}>
              <div>
                <Nav as="ul">{createLinks(prop.views)}</Nav>
              </div>
            </Collapse>
          </Nav.Item>
        );
      }

      //Fill in the client's name
      let linkName = prop.name;
      let linkPath = prop.path;
      if (groupName === "external" && prop.name === "Client Details") {
        linkName = clientName || userClientId;
        linkPath = prop.path.replace(":clientId", userClientId);
      }
      if (prop.showInNav) {
        return (
          <Nav.Item
            className={activeRoute(prop.layout + linkPath)}
            key={key}
            as="li"
          >
            <Nav.Link to={prop.layout + linkPath} as={Link}>
              {prop.icon ? (
                <>
                  <i className={prop.icon} />
                  <p>{linkName}</p>
                </>
              ) : (
                <>
                  <span className="sidebar-mini">{prop.mini}</span>
                  <span className="sidebar-normal">{linkName}</span>
                </>
              )}
            </Nav.Link>
          </Nav.Item>
        );
      }
    });
  };
  // verifies if routeName is the one active (in browser input)
  const activeRoute = (routeName) => {
    return location.pathname === routeName ? "active" : "";
  };
  return (
    <>
      <div className="sidebar" data-color={"white"}>
        <div className="sidebar-wrapper">
          <div className="logo">
            <a
              className="simple-text logo-mini"
              href="https://www.nextgen.com/"
            >
              <div className="logo-img">
                <img
                  src={require("assets/img/Logo_Mirth.svg").default}
                  alt="react-logo"
                />
              </div>
            </a>
            {isDark ? (
              <a
                className="simple-text logo-normal"
                href="https://www.nextgen.com/"
              >
                <img
                  style={{ width: "185px" }}
                  src={require("assets/img/MCC_Logo_Text_Dark.svg").default}
                  alt="react-logo"
                />
              </a>
            ) : (
              <a
                className="simple-text logo-normal"
                href="https://www.nextgen.com/"
              >
                <img
                  style={{ width: "185px" }}
                  src={require("assets/img/MCC_Logo_Text.svg").default}
                  alt="react-logo"
                />
              </a>
            )}
          </div>
          <Nav as="ul">{createLinks(routes)}</Nav>
        </div>
      </div>
    </>
  );
}

let linkPropTypes = {
  path: PropTypes.string,
  layout: PropTypes.string,
  name: PropTypes.string,
  component: PropTypes.oneOfType([PropTypes.func, PropTypes.element]),
};

Sidebar.defaultProps = {
  routes: [],
};

Sidebar.propTypes = {
  background: PropTypes.oneOf([
    "black",
    "azure",
    "green",
    "orange",
    "red",
    "purple",
  ]),
  routes: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        ...linkPropTypes,
        icon: PropTypes.string,
      }),
      PropTypes.shape({
        collapse: true,
        path: PropTypes.string,
        name: PropTypes.string,
        state: PropTypes.string,
        icon: PropTypes.string,
        views: PropTypes.shape({
          ...linkPropTypes,
          mini: PropTypes.string,
        }),
      }),
    ]),
  ),
};

export default Sidebar;
